// src/pages/client/ClientReports.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiFileText,
  FiDownload,
  FiFilter,
  FiSearch,
  FiCalendar,
  FiUser,
  FiDroplet,
  FiFeather,
  FiCheckCircle,
  FiBarChart2,
  FiPlus,
  FiMessageSquare,
  FiX,
  FiTrash2,
} from "react-icons/fi";
import { useClient } from "../context/ClientContext";
import axios from "axios";

function ClientReports() {
  const { client } = useClient();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);

  const reportTypes = [
    { value: "all", label: "All Reports", icon: FiFileText },
    { value: "soil-test", label: "Soil Tests", icon: FiDroplet },
    { value: "farm-assessment", label: "Farm Assessments", icon: FiFeather },
    { value: "recommendation", label: "Recommendations", icon: FiCheckCircle },
    { value: "progress", label: "Progress Reports", icon: FiBarChart2 },
    { value: "client-update", label: "Client Update", icon: FiUser },
    { value: "consultation", label: "Consultation", icon: FiFileText },
  ];

  const clientReportTypes = [
    { value: "client-update", label: "Client Update", icon: "👤" },
    { value: "progress", label: "Progress Report", icon: "📈" },
    { value: "farm-assessment", label: "Farm Assessment", icon: "🏞️" },
    { value: "consultation", label: "Consultation Request", icon: "💼" },
  ];

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, typeFilter]);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.get(
        "https://agri-back-mauve.vercel.app/api/client-portal/reports",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const createReport = async (reportData) => {
    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.post(
        "https://agri-back-mauve.vercel.app/api/client-portal/reports",
        reportData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setReports((prev) => [response.data.data, ...prev]);
        setShowCreateModal(false);
        alert("Report submitted successfully!");
      }
    } catch (error) {
      console.error("Error creating report:", error);
      alert(
        "Failed to create report: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const addResponse = async (reportId, responseData) => {
    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.post(
        `https://agri-back-mauve.vercel.app/api/client-portal/reports/${reportId}/responses`,
        responseData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setReports((prev) =>
          prev.map((report) =>
            report._id === reportId ? response.data.data : report
          )
        );
        setShowResponseModal(false);
        setSelectedReport(null);
        alert("Response added successfully!");
      }
    } catch (error) {
      console.error("Error adding response:", error);
      alert(
        "Failed to add response: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const filterReports = () => {
    let filtered = reports;

    if (typeFilter !== "all") {
      filtered = filtered.filter((report) => report.type === typeFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (report) =>
          report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.recommendations?.some((rec) =>
            rec.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    setFilteredReports(filtered);
  };

  const getReportIcon = (type) => {
    const reportType = reportTypes.find((rt) => rt.value === type);
    const Icon = reportType ? reportType.icon : FiFileText;
    return <Icon className="w-5 h-5" />;
  };

  const getReportColor = (type) => {
    switch (type) {
      case "soil-test":
        return "orange";
      case "farm-assessment":
        return "green";
      case "recommendation":
        return "blue";
      case "progress":
        return "purple";
      case "client-update":
        return "indigo";
      case "consultation":
        return "pink";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const downloadReport = async (report) => {
    if (report.fileUrl) {
      window.open(report.fileUrl, "_blank");
    } else {
      const reportData = {
        title: report.title,
        type: report.type,
        date: formatDate(report.createdAt),
        content: report.content,
        recommendations: report.recommendations,
        data: report.data,
        responses: report.responses,
      };

      const blob = new Blob([JSON.stringify(reportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, "_")}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                Reports & Communication
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {reports.length} total reports
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <FiPlus className="mr-2" />
                Create Report
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 min-w-[300px]">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <FiFilter className="text-gray-400" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {reportTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        {filteredReports.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredReports.map((report, index) => (
              <motion.div
                key={report._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Report Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-12 h-12 bg-${getReportColor(
                          report.type
                        )}-100 rounded-lg flex items-center justify-center text-${getReportColor(
                          report.type
                        )}-600`}
                      >
                        {getReportIcon(report.type)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {report.title}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-500 capitalize">
                            {report.type.replace("-", " ")} Report
                          </span>
                          <span
                            className={`px-2 py-1 text-xs rounded-full capitalize ${
                              report.createdBy === "admin"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {report.createdBy}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Report Details */}
                  <div className="space-y-3 text-sm mb-4">
                    <div className="flex items-center text-gray-600">
                      <FiCalendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{formatDate(report.createdAt)}</span>
                    </div>

                    {report.projectId && (
                      <div className="flex items-center text-gray-600">
                        <FiBarChart2 className="w-4 h-4 mr-2 text-gray-400" />
                        <span>Project: {report.projectId.title}</span>
                      </div>
                    )}
                  </div>

                  {/* Content Preview */}
                  {report.content && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {report.content}
                      </p>
                    </div>
                  )}

                  {/* Responses Count */}
                  {report.responses && report.responses.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <FiMessageSquare className="w-4 h-4 mr-1 text-gray-400" />
                        <span>{report.responses.length} responses</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedReport(report)}
                      className="flex-1 bg-green-600 text-white py-2 px-3 rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      View Report
                    </button>
                    <button
                      onClick={() => downloadReport(report)}
                      className="p-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                      title="Download"
                    >
                      <FiDownload size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No reports found
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || typeFilter !== "all"
                ? "Try adjusting your search or filters"
                : "You don't have any reports yet. Create your first report or wait for consultant updates."}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Create First Report
            </button>
          </motion.div>
        )}

        {/* Report Detail Modal */}
        {selectedReport && !showResponseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">
                  {selectedReport.title}
                </h2>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="p-6">
                {/* Report Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-16 h-16 bg-${getReportColor(
                        selectedReport.type
                      )}-100 rounded-lg flex items-center justify-center text-${getReportColor(
                        selectedReport.type
                      )}-600`}
                    >
                      {getReportIcon(selectedReport.type)}
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 capitalize">
                        {selectedReport.type.replace("-", " ")} Report
                      </div>
                      <div className="text-lg font-semibold text-gray-900">
                        {selectedReport.title}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowResponseModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <FiMessageSquare className="mr-2" />
                      Respond
                    </button>
                    <button
                      onClick={() => downloadReport(selectedReport)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                    >
                      <FiDownload size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>

                {/* Report Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500">Date Created</div>
                    <div className="font-medium text-gray-900">
                      {formatDate(selectedReport.createdAt)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500">Created By</div>
                    <div className="font-medium text-gray-900">
                      {selectedReport.createdByName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedReport.createdByEmail}
                    </div>
                  </div>
                  {selectedReport.projectId && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500">
                        Related Project
                      </div>
                      <div className="font-medium text-gray-900">
                        {selectedReport.projectId.title}
                      </div>
                    </div>
                  )}
                </div>

                {/* Report Content */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Report Content
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-gray-700 whitespace-pre-wrap">
                      {selectedReport.content}
                    </div>
                  </div>
                </div>

                {/* Image Links */}
                {selectedReport.imageLinks &&
                  selectedReport.imageLinks.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Attached Images
                      </h3>
                      <div className="space-y-2">
                        {selectedReport.imageLinks.map((link, index) => (
                          <a
                            key={index}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-blue-600 hover:text-blue-700"
                          >
                            {link}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Recommendations */}
                {selectedReport.recommendations &&
                  selectedReport.recommendations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Recommendations
                      </h3>
                      <div className="space-y-3">
                        {selectedReport.recommendations.map(
                          (recommendation, index) => (
                            <div
                              key={index}
                              className="flex items-start p-4 border border-green-200 rounded-lg bg-green-50"
                            >
                              <FiCheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                              <div className="text-gray-700">
                                {recommendation}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Responses */}
                {selectedReport.responses &&
                  selectedReport.responses.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        Responses ({selectedReport.responses.length})
                      </h3>
                      <div className="space-y-4">
                        {selectedReport.responses.map((response, index) => (
                          <div
                            key={index}
                            className={`p-4 rounded-lg border ${
                              response.createdBy === "admin"
                                ? "bg-green-50 border-green-200"
                                : "bg-blue-50 border-blue-200"
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2">
                                <span
                                  className={`px-2 py-1 text-xs rounded-full capitalize ${
                                    response.createdBy === "admin"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {response.createdByName || response.createdBy}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {formatDate(response.createdAt)}
                                </span>
                              </div>
                            </div>
                            <div className="text-gray-700 whitespace-pre-wrap">
                              {response.content}
                            </div>
                            {response.imageLinks &&
                              response.imageLinks.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-sm font-medium text-gray-900 mb-2">
                                    Attached Images:
                                  </div>
                                  <div className="space-y-1">
                                    {response.imageLinks.map(
                                      (link, linkIndex) => (
                                        <a
                                          key={linkIndex}
                                          href={link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block text-sm text-blue-600 hover:text-blue-700 truncate"
                                        >
                                          {link}
                                        </a>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Create Report Modal */}
        {showCreateModal && (
          <CreateReportModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={createReport}
            reportTypes={clientReportTypes}
          />
        )}

        {/* Add Response Modal */}
        {showResponseModal && selectedReport && (
          <AddResponseModal
            report={selectedReport}
            onClose={() => {
              setShowResponseModal(false);
              setSelectedReport(null);
            }}
            onSubmit={(responseData) =>
              addResponse(selectedReport._id, responseData)
            }
          />
        )}
      </div>
    </div>
  );
}

// Create Report Modal Component
const CreateReportModal = ({ onClose, onSubmit, reportTypes }) => {
  const [formData, setFormData] = useState({
    title: "",
    type: "client-update",
    content: "",
    imageLinks: [""],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      imageLinks: formData.imageLinks.filter((link) => link.trim()),
    });
  };

  const addImageLink = () => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: [...prev.imageLinks, ""],
    }));
  };

  const removeImageLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: prev.imageLinks.filter((_, i) => i !== index),
    }));
  };

  const updateImageLink = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: prev.imageLinks.map((link, i) =>
        i === index ? value : link
      ),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Create New Report</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Enter report title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type *
            </label>
            <select
              required
              value={formData.type}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, type: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {reportTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Content *
            </label>
            <textarea
              required
              value={formData.content}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, content: e.target.value }))
              }
              rows={6}
              placeholder="Enter detailed report content..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Links (Optional)
            </label>
            <div className="space-y-2">
              {formData.imageLinks.map((link, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => updateImageLink(index, e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formData.imageLinks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageLink(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addImageLink}
                className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
              >
                <FiPlus className="mr-1" size={14} />
                Add Image Link
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Submit Report
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Add Response Modal Component
const AddResponseModal = ({ report, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    content: "",
    imageLinks: [""],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      imageLinks: formData.imageLinks.filter((link) => link.trim()),
    });
  };

  const addImageLink = () => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: [...prev.imageLinks, ""],
    }));
  };

  const removeImageLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: prev.imageLinks.filter((_, i) => i !== index),
    }));
  };

  const updateImageLink = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      imageLinks: prev.imageLinks.map((link, i) =>
        i === index ? value : link
      ),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Add Response</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response to: {report.title}
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Response *
            </label>
            <textarea
              required
              value={formData.content}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, content: e.target.value }))
              }
              rows={6}
              placeholder="Enter your response..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Links (Optional)
            </label>
            <div className="space-y-2">
              {formData.imageLinks.map((link, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => updateImageLink(index, e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  {formData.imageLinks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageLink(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addImageLink}
                className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
              >
                <FiPlus className="mr-1" size={14} />
                Add Image Link
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Send Response
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ClientReports;
